import React from 'react';

import { NavLinkButton } from '@shared/components/bootstrap';
import { Currency } from '@shared/components/helpers';
import { Percent } from '@shared/components/helpers/percent';
import { UserRole } from '@shared/types/user_role';

import { BanInfo as AccountBanInfo } from '@admin/components/account/ban_info';
import { BanUpdate as AccountBanUpdate } from '@admin/components/account/ban_update';
import { CancelButton as AccountCancelButton } from '@admin/components/account/cancel_button';
import { CancelDueToBankruptcyButton } from '@admin/components/account/cancel_due_to_bankruptcy_button';
import { Materials as AccountMaterials } from '@admin/components/account/materials';
import { OrderCancellationPolicies as AccountOrderCancellationPolicies } from '@admin/components/account/order_cancellation_policies';
import { AccountReferralCode } from '@admin/components/account/referral_code';
import { StorageLadderModal as AccountStorageLadderModal } from '@admin/components/account/storage_ladder_modal';
import { AccountSummary } from '@admin/components/account/summary';
import { TermCommitments as AccountTermCommitments } from '@admin/components/account/term_commitments';
import { AccountRateAdjustments } from '@admin/components/account_rate_adjustments/account_rate_adjustments';
import { Tags as AccountTags } from '@admin/components/account_tags/tags';
import { State as AccountStateLabel } from '@admin/components/accounts/state';
import { AccountUsagesWidget } from '@admin/components/accounts/usages_widget';
import { CopyURL } from '@admin/components/helpers/copy_url';
import { Permission } from '@admin/components/helpers/permission';
import { Roles } from '@admin/components/helpers/roles';
import { titleize } from '@admin/components/helpers/titleize';
import { OrdersMissingSignature } from '@admin/components/orders/signatures/orders_missing_signature';
import { Summary as SubscriptionsSummary } from '@admin/components/subscriptions/summary';
import {
  ACCOUNT_CANCELLATION_STATUS,
  ACCOUNT_SUSPENSION_STATUS,
} from '@admin/libraries/access_control_catalog.generated';
import type { IAccount } from '@admin/types/account';
import type { SubscriptionSummaryList } from '@admin/utils/account_subscriptions';
import { formatAffirmation } from '@admin/utils/format_affirmation';

const CARDLESS_TAG = 'Cardless';

const PartnerManagedLabel: React.FC = () => (
  <span className="label label-info" style={{ marginLeft: 8 }}>
    Partner-managed
  </span>
);

export const AccountOverviewPanel: React.FC<{
  account: IAccount;
  storagePlans: SubscriptionSummaryList;
  protectionPlans: SubscriptionSummaryList;
  onScrollToSubscriptions(): void;
  onCanceled(): void;
  onSuspended(): void;
  onBanUpdated(banned: boolean): void;
  onUnsuspend(): void;
}> = ({
  account,
  storagePlans,
  protectionPlans,
  onScrollToSubscriptions,
  onCanceled,
  onSuspended,
  onBanUpdated,
  onUnsuspend,
}) => {
  const coupon = account.coupon;
  const accountID = Number(account.id);

  const cardless = account.tags?.includes(CARDLESS_TAG);

  return (
    <div className="panel account-panel">
      <div className="panel-heading">
        <h3 className="panel-title">
          Account
          {account.partner_managed && <PartnerManagedLabel />}
        </h3>
      </div>
      <div className="panel-body" style={{ minHeight: 169 }}>
        <div className="position-relative">
          <strong>Account #:</strong> #{account.number} <span className="stripe-id">{account.stripe_id}</span>
          {account.id && <AccountTags accountID={accountID} />}
          <CopyURL />
        </div>

        {cardless && account.onboarding_confirmed != null && (
          <div>
            <strong>Onboarding Confirmed: </strong>
            <span className={account.onboarding_confirmed ? undefined : 'text-warning'}>
              {formatAffirmation(account.onboarding_confirmed)}
            </span>
          </div>
        )}

        <div>
          <strong>State:</strong> <AccountStateLabel account={{ state: account.state }} />
          {account.delinquent_reason && <span>({account.delinquent_reason})</span>}
        </div>

        <div>
          <strong>Current Subscription:</strong>{' '}
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              onScrollToSubscriptions();
            }}
          >
            <SubscriptionsSummary subscriptions={storagePlans} showCost={true} />
          </a>
          <AccountStorageLadderModal accountID={String(account.id)} />
        </div>

        <div>
          <strong>Protection Plan:</strong>{' '}
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              onScrollToSubscriptions();
            }}
          >
            <SubscriptionsSummary subscriptions={protectionPlans} showCost={true} />
          </a>
        </div>

        <div>
          <strong>Current Coupon:</strong> {coupon?.name ? <span>{titleize(coupon.name)}</span> : <span>None</span>}
          {coupon && (
            <span>
              {coupon.percent != null && coupon.percent > 0 && (
                <span>
                  {' '}
                  (<Percent value={coupon.percent} /> off)
                </span>
              )}
              {coupon.amount != null && coupon.amount > 0 && (
                <span>
                  {' '}
                  (<Currency value={coupon.amount} /> off)
                </span>
              )}
            </span>
          )}
        </div>

        <div>
          <AccountSummary accountID={accountID} />
        </div>

        <div>
          <AccountOrderCancellationPolicies accountID={accountID} />
        </div>

        <OrdersMissingSignature accountID={String(account.id)} />
        <AccountUsagesWidget accountID={String(account.id)} />
        <AccountTermCommitments accountID={String(account.id)} />
        <AccountMaterials accountID={String(account.id)} />
        {account.id && <AccountRateAdjustments accountID={String(account.id)} />}
        <AccountBanInfo accountID={String(account.id)} />
        <AccountReferralCode accountID={accountID} />
      </div>

      <div className="panel-footer text-right">
        <Permission
          resource={ACCOUNT_CANCELLATION_STATUS.resource}
          action={ACCOUNT_CANCELLATION_STATUS.actions.update}
          denied={null}
        >
          <CancelDueToBankruptcyButton
            accountID={String(account.id)}
            accountNumber={account.number}
            accountState={account.state}
            onCanceled={onCanceled}
          />
        </Permission>
        <Permission
          resource={ACCOUNT_SUSPENSION_STATUS.resource}
          action={ACCOUNT_SUSPENSION_STATUS.actions.update}
          denied={null}
        >
          {account.is_unsuspendable && (
            <button type="button" className="btn btn-danger" onClick={onUnsuspend}>
              Activate Account
            </button>
          )}
        </Permission>
        <Permission
          resource={ACCOUNT_CANCELLATION_STATUS.resource}
          action={ACCOUNT_CANCELLATION_STATUS.actions.update}
          denied={null}
        >
          {account.is_cancelable && (
            <AccountCancelButton
              accountID={String(account.id)}
              accountNumber={account.number}
              onCanceled={onCanceled}
            />
          )}
        </Permission>
        <Roles show={[UserRole.Admin, UserRole.Collections]}>
          <AccountBanUpdate accountID={String(account.id)} onBanUpdated={onBanUpdated} />
        </Roles>
        <NavLinkButton to={`/accounts/${account.id}/inventory`} kind="primary">
          Inventory
        </NavLinkButton>
      </div>
    </div>
  );
};
